import { Download } from 'lucide-react';
import { useState } from 'react';
import { Field, NativeSelect } from '@/components/form';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useMe } from '@/lib/clients';
import { formatDateTime } from '@/lib/format';
import {
  IRN_CANCEL_REASONS,
  TEST_CONNECTION_HINT,
  useEInvoicePreview,
  useIrnAction,
} from '@/lib/gst';
import { isIndia } from '@/lib/org-locale';
import { cn } from '@/lib/utils';

/**
 * E-invoice (IRP) strip on an approved India invoice or credit note (document 06 T9): the IRN
 * and acknowledgement once registered, else what the IRP rules still find, and the actions
 * Generate IRN, Download JSON and Cancel IRN (within 24 hours). Renders nothing for UAE clients.
 */
export function IrnPanel({ documentId, number }: { documentId: string; number: string | null }) {
  const india = isIndia();
  const q = useEInvoicePreview(documentId, india);
  const action = useIrnAction(documentId);
  const me = useMe();
  const [cancelling, setCancelling] = useState(false);
  const [reasonCode, setReasonCode] = useState(2);
  const [reason, setReason] = useState('');
  if (!india || !q.data) return null;
  const { irn, validation } = q.data;
  const cancelled = irn ? null : (q.data.cancelledIrn ?? null);
  const staff = me.data?.isStaff ?? false;
  const within24h = irn ? Date.now() - new Date(irn.ackDate).getTime() < 24 * 3600 * 1000 : false;

  const download = () => {
    const url = URL.createObjectURL(new Blob([q.data.json], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `INV-01_${number ?? documentId}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div
      data-testid="irn-panel"
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-md border px-4 py-2.5 text-sm',
        !irn && !validation.valid ? 'border-[#FCD34D] bg-[#FFFBEB]' : 'bg-card',
      )}
    >
      <span className="font-medium">E-invoice (IRP)</span>
      {irn?.provider === 'mock' ? (
        <span
          className="rounded-full bg-[#FEF3F2] px-2 py-0.5 text-xs font-medium text-[#B42318]"
          title={TEST_CONNECTION_HINT}
          data-testid="irn-test"
        >
          Test IRN
        </span>
      ) : null}
      {irn ? (
        <span className="min-w-48 flex-1 text-muted-foreground">
          <span className="text-foreground">IRN generated</span> · Ack. no.{' '}
          <span className="tnum">{irn.ackNo}</span> on {formatDateTime(irn.ackDate)}
          <span className="block truncate font-mono text-xs" title={irn.irn}>
            {irn.irn}
          </span>
          This document can no longer be edited or voided; cancel the IRN within 24 hours or issue a
          credit note.
        </span>
      ) : cancelled ? (
        <span className="min-w-48 flex-1 text-muted-foreground" data-testid="irn-cancelled">
          <span className="text-foreground">IRN cancelled</span> on{' '}
          {formatDateTime(cancelled.cancelledAt)} · {cancelled.cancelReason}
          <span className="block truncate font-mono text-xs line-through" title={cancelled.irn}>
            {cancelled.irn}
          </span>
          {validation.valid
            ? 'The document can be edited or voided again. Generate a new IRN once it is correct.'
            : 'Fix what the IRP rules find below the form, then generate a new IRN.'}
        </span>
      ) : validation.valid && validation.warnings.length ? (
        <span className="min-w-48 flex-1 text-muted-foreground">
          Not registered with the IRP yet.
          <ul className="list-disc pl-5 text-xs">
            {validation.warnings.map((w) => (
              <li key={w.rule}>
                {w.message} ({w.rule})
              </li>
            ))}
          </ul>
        </span>
      ) : validation.valid ? (
        <span className="min-w-48 flex-1 text-muted-foreground">
          Not registered with the IRP yet.
        </span>
      ) : (
        <span className="min-w-48 flex-1">
          Not ready for the IRP:
          <ul className="list-disc pl-5 text-xs">
            {validation.errors.map((e) => (
              <li key={`${e.rule}-${e.line ?? 0}`}>
                {e.line ? `Line ${e.line}: ` : ''}
                {e.message} ({e.rule})
              </li>
            ))}
          </ul>
        </span>
      )}
      <Button variant="link" size="sm" className="px-1" onClick={download}>
        <Download className="size-4" /> Download JSON
      </Button>
      {staff && !irn ? (
        <Button
          size="sm"
          disabled={!validation.valid || action.isPending}
          onClick={() =>
            action.mutate(
              { kind: 'generate' },
              {
                onSuccess: (r) =>
                  toast(`IRN generated for ${number ?? 'the document'} (ack. ${r.ackNo}).`),
                onError: (e) => toast(e.message),
              },
            )
          }
        >
          {cancelled ? 'Generate new IRN' : 'Generate IRN'}
        </Button>
      ) : null}
      {staff && irn && within24h ? (
        <Button size="sm" variant="outline" onClick={() => setCancelling(true)}>
          Cancel IRN
        </Button>
      ) : null}
      <Dialog open={cancelling} onOpenChange={setCancelling}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel IRN</DialogTitle>
            <DialogDescription>
              The IRP allows cancelling an IRN within 24 hours. After that, issue a credit note.
            </DialogDescription>
          </DialogHeader>
          <Field label="Reason">
            {(id) => (
              <NativeSelect
                id={id}
                value={String(reasonCode)}
                onChange={(e) => setReasonCode(Number(e.target.value))}
              >
                {IRN_CANCEL_REASONS.map(([v, label]) => (
                  <option key={v} value={v}>
                    {label}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label="Remark">
            {(id) => (
              <Input
                id={id}
                value={reason}
                maxLength={100}
                placeholder="Wrong place of supply"
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelling(false)}>
              Keep it
            </Button>
            <Button
              disabled={reason.trim().length < 3 || action.isPending}
              onClick={() =>
                action.mutate(
                  { kind: 'cancel', reasonCode, remark: reason.trim() },
                  {
                    onSuccess: () => {
                      toast('IRN cancelled.');
                      setCancelling(false);
                    },
                    onError: (e) => toast(e.message),
                  },
                )
              }
            >
              Cancel IRN
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
